import React, { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable } from '../../lib/react-native-compat';
import { jobPhaseCodesAtom } from '../../store/company';
import { loadFieldReportQtyHistory } from '../../db/company';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import { isBlankRow } from './sectionDefs';
import {
    moduleForCode, parseStation, formatStation, pairFootage,
    installedToday, installedBySize,
} from './qtyModules';
import AutoGrowTextarea from './AutoGrowTextarea';

// Pipeline quantity tracking. One collapsed card per cost code picked on the
// Work tab; tapping a card expands the module the Pipeline Tracker workbook
// prescribes for that code (station pairs, counts, tests, NDT, mats, weather,
// crew days). Codes the tracker doesn't track get no card — the point is to
// show the field only what they must type.
//
// Detail rows live in sections.qtyDetail (JSON); every edit also rebuilds the
// flat sections.quantities rows (typed PC.Field_Report_Quantity) so review,
// sheet view, submit payload and the to-date/7-day history SQL all keep
// working off the same numbers. A '__meta' detail row carries the job's pipe
// size list; the history API returns the latest one so new reports inherit it.

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};
const fmtN = (n) => (n == null || !Number.isFinite(n)) ? '—'
    : (Math.round(n * 10) / 10).toLocaleString();

const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 600, color: '#64748B',
    marginBottom: 3, fontFamily: 'inherit',
};
const inputStyle = {
    width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '8px 10px',
    borderRadius: 8, border: '1px solid #CBD5E1', fontSize: 14, color: '#0F172A',
    backgroundColor: '#fff', fontFamily: 'inherit', outline: 'none',
};
const statStyle = { fontSize: 11, color: '#64748B' };
const statValStyle = { fontSize: 13, fontWeight: 700, color: '#0F172A', fontVariantNumeric: 'tabular-nums' };

const META_CODE = '__meta';

// Flat typed rows derived from the detail rows. One row per cost code — plus
// one per pipe size (size in the Task column) when the code splits by size,
// with any unsized remainder on the base row. BudgetQty rides only the first
// row of a code so per-code MAX/SUM reads stay sane.
export const buildFlatQuantities = (detailRows, history, modOf) => {
    const flat = [];
    for (const row of detailRows || []) {
        if (!row?.costCode || row.costCode === META_CODE) continue;
        if (isBlankRow({ ...row, costCode: '' })) continue; // untouched card
        const mod = modOf ? modOf(row.costCode) : moduleForCode(row.costCode);
        if (!mod) continue;
        const today = installedToday(row, mod);
        const bySize = installedBySize(row, mod) || {};
        const sizedTotal = Object.values(bySize).reduce((t, v) => t + v, 0);
        const unsized = Math.round((today - sizedTotal) * 100) / 100;
        const hist = (key) => history?.[`${row.costCode}|${key}`] || {};
        const mk = (task, qty, first) => ({
            costCode: row.costCode,
            task,
            unit: mod.uom,
            budgetQty: first ? (row.budgetQty ?? '') : '',
            installedToday: qty === 0 && !first ? '' : String(qty),
            installedToDate: String((num(hist(task).toDate) || 0) + qty),
            reworkedToday: '',
            mhToday: '',
        });
        const sizes = Object.keys(bySize);
        if (sizes.length === 0) {
            flat.push(mk('', today, true));
        } else {
            sizes.forEach((s, i) => flat.push(mk(s, bySize[s], i === 0)));
            if (unsized > 0) flat.push(mk('', unsized, false));
        }
    }
    return flat;
};

const Chip = ({ label, active, onPress, small }) => (
    <Pressable
        onPress={onPress}
        style={{
            paddingTop: small ? 3 : 6, paddingBottom: small ? 3 : 6,
            paddingLeft: small ? 8 : 12, paddingRight: small ? 8 : 12,
            borderRadius: 999, cursor: 'pointer',
            backgroundColor: active ? '#1D4ED8' : '#fff',
            border: active ? '1px solid #1D4ED8' : '1px solid #CBD5E1',
        }}
    >
        <Text style={{ fontSize: small ? 11 : 12, fontWeight: '600', color: active ? '#fff' : '#475569' }}>{label}</Text>
    </Pressable>
);

const NumberField = ({ label, value, onChange, readOnly, placeholder, suffix }) => (
    <div>
        <label style={labelStyle}>{label}</label>
        <div style={{ position: 'relative' }}>
            <input
                type="number"
                inputMode="decimal"
                value={value ?? ''}
                disabled={readOnly}
                placeholder={placeholder || '0'}
                onChange={(e) => onChange(e.target.value)}
                style={{ ...inputStyle, paddingRight: suffix ? 40 : 10 }}
            />
            {suffix ? (
                <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94A3B8' }}>{suffix}</span>
            ) : null}
        </div>
    </div>
);

const QuantityCards = ({ readOnly }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);
    const [expanded, setExpanded] = useState({});
    const [historyRes, setHistoryRes] = useState(null);
    const [sizeDraft, setSizeDraft] = useState('');
    const [editSizes, setEditSizes] = useState(false);

    const detail = report?.sections?.qtyDetail || [];
    const metaRow = detail.find((r) => r?.costCode === META_CODE);

    // Cost codes picked on the Work tab, in pick order, tracker-mapped only.
    const workCodes = useMemo(() => {
        const seen = new Set();
        const out = [];
        for (const r of report?.sections?.work || []) {
            const code = String(r?.costCode || '').trim();
            if (!code || seen.has(code)) continue;
            seen.add(code);
            out.push(code);
        }
        return out;
    }, [report?.sections?.work]);
    // Resolve each code's module: exact tracker mapping first, then inference
    // from the phase description for codes the workbook doesn't list (client
    // WBS, 160xxx above-ground codes…). "Not in tracker" codes stay null.
    const modOf = (code) => moduleForCode(code, phaseCodes?.[code]);
    const trackedCodes = workCodes.filter((c) => modOf(c));
    const untrackedCodes = workCodes.filter((c) => !modOf(c));

    // Prior installs for this job: { 'code|size': { toDate, last7 } }, the
    // latest budget qty per code, and the job's pipe-size list.
    useEffect(() => {
        let alive = true;
        if (!report?.buId) return undefined;
        loadFieldReportQtyHistory({
            buId: report.buId,
            reportId: report.reportId,
            reportDate: report.reportDate,
        }).then((res) => { if (alive && res && !res.error) setHistoryRes(res); });
        return () => { alive = false; };
    }, [report?.buId, report?.reportId, report?.reportDate]);

    const history = useMemo(() => {
        const out = {};
        for (const h of historyRes?.history || []) {
            out[`${String(h.CostCode || '').trim()}|${String(h.Task || '').trim()}`] = {
                toDate: num(h.ToDate), last7: num(h.Last7),
            };
        }
        return out;
    }, [historyRes]);
    const codeHist = (code) => {
        let toDate = 0; let last7 = 0;
        Object.entries(history).forEach(([k, v]) => {
            if (k.split('|')[0] === code) { toDate += v.toDate; last7 += v.last7; }
        });
        return { toDate, last7 };
    };
    const budgets = useMemo(() => {
        const out = {};
        for (const b of historyRes?.budgets || []) {
            const q = num(b.BudgetQty);
            if (q) out[String(b.CostCode || '').trim()] = q;
        }
        // Schedule & Baseline page totals win over budgets carried forward
        // from prior reports (absent until the function deploy carries the
        // fifth recordset).
        for (const s of historyRes?.schedule || []) {
            const q = num(s.Budget_Qty);
            if (q) out[String(s.Cost_Code || '').trim()] = q;
        }
        return out;
    }, [historyRes]);

    // Pipe sizes: report meta row wins; else the latest prior report's list.
    const sizes = useMemo(() => {
        if (Array.isArray(metaRow?.sizes)) return metaRow.sizes;
        if (Array.isArray(historyRes?.sizes)) return historyRes.sizes;
        return [];
    }, [metaRow, historyRes]);

    const getRow = (code) => detail.find((r) => r?.costCode === code) || { costCode: code };

    const writeDetail = (nextDetail) => {
        setReport((rep) => ({
            ...rep,
            sections: {
                ...rep.sections,
                qtyDetail: nextDetail,
                quantities: buildFlatQuantities(nextDetail, history, modOf),
            },
        }));
        setDirty(true);
    };

    const updateRow = (code, patch) => {
        const idx = detail.findIndex((r) => r?.costCode === code);
        const next = detail.slice();
        if (idx >= 0) next[idx] = { ...next[idx], ...patch };
        else next.push({ costCode: code, ...patch });
        writeDetail(next);
    };

    const setSizes = (list) => {
        const clean = [...new Set(list.map((s) => String(s).trim()).filter(Boolean))];
        const idx = detail.findIndex((r) => r?.costCode === META_CODE);
        const next = detail.slice();
        if (idx >= 0) next[idx] = { ...next[idx], sizes: clean };
        else next.push({ costCode: META_CODE, sizes: clean });
        writeDetail(next);
    };

    // Prefill budget qty once per code — from the Schedule & Baseline page's
    // totals when present, else the job's latest prior report.
    useEffect(() => {
        if (readOnly || !historyRes) return;
        const missing = trackedCodes.filter((c) => {
            const r = detail.find((d) => d?.costCode === c);
            return budgets[c] && (r?.budgetQty ?? '') === '';
        });
        if (!missing.length) return;
        let next = detail.slice();
        for (const code of missing) {
            const idx = next.findIndex((r) => r?.costCode === code);
            if (idx >= 0) next[idx] = { ...next[idx], budgetQty: String(budgets[code]) };
            else next.push({ costCode: code, budgetQty: String(budgets[code]) });
        }
        writeDetail(next);
    }, [historyRes, trackedCodes.join('|')]);

    // History can land after the user already typed today's numbers — refresh
    // the derived flat rows' to-date figures without marking the report dirty.
    useEffect(() => {
        if (!historyRes) return;
        setReport((rep) => {
            const d = rep?.sections?.qtyDetail || [];
            if (!d.some((r) => r?.costCode && r.costCode !== META_CODE)) return rep;
            return { ...rep, sections: { ...rep.sections, quantities: buildFlatQuantities(d, history, modOf) } };
        });
    }, [history]);

    if (!trackedCodes.length) {
        return (
            <View style={{ borderRadius: 12, border: '1px dashed #CBD5E1', padding: 24, alignItems: 'center', backgroundColor: '#F8FAFC' }}>
                <Text style={{ fontSize: 14, color: '#64748B', textAlign: 'center' }}>
                    {workCodes.length
                        ? 'None of the phases picked on the Work tab track daily quantities — nothing to enter here.'
                        : 'Pick the phases worked on the Work tab first — each one that tracks quantities gets its entry card here.'}
                </Text>
            </View>
        );
    }

    // ── Per-module editors ──────────────────────────────────────────────────
    const stationEditor = (code, mod, row) => {
        const pairs = row.pairs?.length ? row.pairs : [{}];
        const setPairs = (next) => updateRow(code, { pairs: next });
        const upd = (i, patch) => {
            const next = pairs.slice();
            next[i] = { ...next[i], ...patch };
            setPairs(next);
        };
        return (
            <div>
                <label style={labelStyle}>Stations worked today (initial → final, e.g. 420+00)</label>
                {pairs.map((p, i) => {
                    const ft = pairFootage(p);
                    return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                            <input
                                type="text" inputMode="decimal" placeholder="Initial (420+00)"
                                value={p.from ?? ''} disabled={readOnly}
                                onChange={(e) => upd(i, { from: e.target.value })}
                                onBlur={() => { const v = parseStation(p.from); if (v != null) upd(i, { from: formatStation(v) }); }}
                                style={{ ...inputStyle, width: 130 }}
                            />
                            <span style={{ color: '#94A3B8', fontSize: 14 }}>→</span>
                            <input
                                type="text" inputMode="decimal" placeholder="Final (275+00)"
                                value={p.to ?? ''} disabled={readOnly}
                                onChange={(e) => upd(i, { to: e.target.value })}
                                onBlur={() => { const v = parseStation(p.to); if (v != null) upd(i, { to: formatStation(v) }); }}
                                style={{ ...inputStyle, width: 130 }}
                            />
                            {mod.pipeSize && sizes.length > 0 && (
                                <select
                                    value={p.size ?? ''} disabled={readOnly}
                                    onChange={(e) => upd(i, { size: e.target.value })}
                                    style={{ ...inputStyle, width: 90, cursor: readOnly ? 'default' : 'pointer', color: p.size ? '#0F172A' : '#94A3B8' }}
                                >
                                    <option value="">Size…</option>
                                    {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            )}
                            <span style={{ fontSize: 12, fontWeight: 700, color: ft ? '#065F46' : '#94A3B8', fontVariantNumeric: 'tabular-nums', minWidth: 70 }}>
                                {ft ? `${fmtN(ft)} LF` : '— LF'}
                            </span>
                            {!readOnly && pairs.length > 1 && (
                                <button
                                    type="button" onClick={() => setPairs(pairs.filter((_, j) => j !== i))}
                                    title="Remove segment"
                                    style={{ border: 'none', background: 'transparent', color: '#DC2626', cursor: 'pointer', fontSize: 15, padding: '2px 4px' }}
                                >×</button>
                            )}
                        </div>
                    );
                })}
                {!readOnly && (
                    <Pressable onPress={() => setPairs([...pairs, {}])} style={{ cursor: 'pointer', alignSelf: 'flex-start', paddingTop: 2, paddingBottom: 6 }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>+ Add segment (second crew / skip)</Text>
                    </Pressable>
                )}
            </div>
        );
    };

    const countEditor = (code, mod, row) => {
        if (mod.pipeSize && sizes.length > 0) {
            const bySize = row.qtyBySize || {};
            return (
                <div>
                    <label style={labelStyle}>{mod.qtyLabel || `Installed today (${mod.uom})`} — by pipe size</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 8 }}>
                        {sizes.map((s) => (
                            <NumberField
                                key={s} label={s} suffix={mod.uom} readOnly={readOnly}
                                value={bySize[s] ?? ''}
                                onChange={(v) => updateRow(code, { qtyBySize: { ...bySize, [s]: v } })}
                            />
                        ))}
                    </div>
                </div>
            );
        }
        return (
            <NumberField
                label={mod.qtyLabel || `Installed today (${mod.uom})`} suffix={mod.uom}
                readOnly={readOnly} value={row.qty ?? ''}
                onChange={(v) => updateRow(code, { qty: v })}
            />
        );
    };

    const moduleEditor = (code, mod, row) => {
        switch (mod.module) {
            case 'stations':
                return stationEditor(code, mod, row);
            case 'count':
                return countEditor(code, mod, row);
            case 'tests':
                return (
                    <NumberField
                        label="Test packages completed today" suffix="PK"
                        readOnly={readOnly} value={row.testsToday ?? ''}
                        onChange={(v) => updateRow(code, { testsToday: v })}
                    />
                );
            case 'ndt':
                return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                        <NumberField label="Welds inspected today" readOnly={readOnly} value={row.qty ?? ''} onChange={(v) => updateRow(code, { qty: v })} />
                        <NumberField label="Rejected today" readOnly={readOnly} value={row.ndtRejected ?? ''} onChange={(v) => updateRow(code, { ndtRejected: v })} />
                        <NumberField label="Repaired today" readOnly={readOnly} value={row.ndtRepaired ?? ''} onChange={(v) => updateRow(code, { ndtRepaired: v })} />
                        <NumberField label="Cut-outs today" readOnly={readOnly} value={row.ndtCutOuts ?? ''} onChange={(v) => updateRow(code, { ndtCutOuts: v })} />
                    </div>
                );
            case 'mats':
                return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                        <NumberField
                            label={mod.matsMode === 'remove' ? 'Mats removed today' : 'Mats installed today'}
                            suffix="EA" readOnly={readOnly} value={row.qty ?? ''}
                            onChange={(v) => updateRow(code, { qty: v })}
                        />
                        <NumberField label="Moved / relocated" suffix="EA" readOnly={readOnly} value={row.matsMoved ?? ''} onChange={(v) => updateRow(code, { matsMoved: v })} />
                        <NumberField label="Trashed" suffix="EA" readOnly={readOnly} value={row.matsTrashed ?? ''} onChange={(v) => updateRow(code, { matsTrashed: v })} />
                    </div>
                );
            case 'weather':
                return (
                    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div>
                            <label style={labelStyle}>Rain day?</label>
                            <div style={{ display: 'flex', gap: 6 }}>
                                {['Y', 'N'].map((v) => (
                                    <Chip
                                        key={v} label={v === 'Y' ? 'Yes' : 'No'} active={(row.rainDay || 'N') === v}
                                        onPress={() => !readOnly && updateRow(code, { rainDay: v })}
                                    />
                                ))}
                            </div>
                        </div>
                        <div style={{ width: 140 }}>
                            <NumberField label="Rainfall" suffix="in" readOnly={readOnly} value={row.rainInches ?? ''} onChange={(v) => updateRow(code, { rainInches: v })} />
                        </div>
                    </div>
                );
            case 'crewDays':
            default:
                return null; // crew days field renders for every module below
        }
    };

    // ── Cards ───────────────────────────────────────────────────────────────
    return (
        <View style={{ paddingBottom: 8 }}>
            <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A', marginBottom: 2 }}>
                Quantities — phases worked today
            </Text>
            <Text style={{ fontSize: 12, color: '#64748B', marginBottom: 10 }}>
                Tap a phase to enter today's numbers. Budget and to-date figures carry over from earlier reports.
            </Text>

            {/* Pipe sizes on the job — feeds the by-size inputs. */}
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                <Text style={{ fontSize: 11, fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pipe sizes:</Text>
                {sizes.length === 0 && <Text style={{ fontSize: 12, color: '#94A3B8' }}>none set</Text>}
                {sizes.map((s) => (
                    <View key={s} style={{ flexDirection: 'row', alignItems: 'center', gap: 2, borderRadius: 999, border: '1px solid #CBD5E1', backgroundColor: '#fff', paddingTop: 3, paddingBottom: 3, paddingLeft: 10, paddingRight: editSizes ? 4 : 10 }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#475569' }}>{s}</Text>
                        {editSizes && !readOnly && (
                            <Pressable onPress={() => setSizes(sizes.filter((x) => x !== s))} style={{ cursor: 'pointer', paddingLeft: 4, paddingRight: 4 }}>
                                <Text style={{ fontSize: 12, color: '#DC2626' }}>×</Text>
                            </Pressable>
                        )}
                    </View>
                ))}
                {!readOnly && editSizes && (
                    <input
                        type="text" value={sizeDraft} placeholder={'add size (12")'}
                        onChange={(e) => setSizeDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && sizeDraft.trim()) {
                                setSizes([...sizes, sizeDraft]);
                                setSizeDraft('');
                            }
                        }}
                        style={{ ...inputStyle, width: 110, minHeight: 30, padding: '4px 8px', fontSize: 12 }}
                    />
                )}
                {!readOnly && (
                    <Pressable onPress={() => setEditSizes(!editSizes)} style={{ cursor: 'pointer' }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>{editSizes ? 'Done' : 'Edit'}</Text>
                    </Pressable>
                )}
            </View>

            {trackedCodes.map((code) => {
                const mod = modOf(code);
                const row = getRow(code);
                const open = !!expanded[code];
                const today = installedToday(row, mod);
                const { toDate, last7 } = codeHist(code);
                const budget = num(row.budgetQty) || budgets[code] || 0;
                const total = toDate + today;
                const pct = budget ? Math.round((total * 1000) / budget) / 10 : null;
                const touched = !isBlankRow({ ...row, costCode: '' });
                return (
                    <div key={code} style={{ borderRadius: 12, border: open ? '1px solid #93C5FD' : '1px solid #E2E8F0', backgroundColor: '#fff', marginBottom: 10, overflow: 'hidden' }}>
                        <Pressable
                            onPress={() => setExpanded({ ...expanded, [code]: !open })}
                            style={{ cursor: 'pointer', padding: 12 }}
                        >
                            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <View style={{ flexShrink: 1, minWidth: 0 }}>
                                    <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F172A' }}>
                                        {`${code} — ${mod.desc || phaseCodes?.[code] || ''}`}
                                    </Text>
                                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 14, rowGap: 2, marginTop: 4 }}>
                                        <Text style={statStyle}>Budget <Text style={statValStyle}>{budget ? `${fmtN(budget)} ${mod.uom}` : '—'}</Text></Text>
                                        <Text style={statStyle}>To date <Text style={statValStyle}>{fmtN(total)}</Text>{pct != null && <Text style={statStyle}>{` (${pct}%)`}</Text>}</Text>
                                        <Text style={statStyle}>Last 7 days <Text style={statValStyle}>{fmtN(last7 + today)}</Text></Text>
                                        {today > 0 && (
                                            <Text style={{ ...statStyle, color: '#065F46' }}>Today <Text style={{ ...statValStyle, color: '#065F46' }}>{`${fmtN(today)} ${mod.uom}`}</Text></Text>
                                        )}
                                    </View>
                                </View>
                                <Text style={{ fontSize: 13, color: touched ? '#065F46' : '#94A3B8', fontWeight: '700' }}>
                                    {open ? '▴' : (touched ? '✓' : '▾')}
                                </Text>
                            </View>
                        </Pressable>

                        {open && (
                            <div style={{ borderTop: '1px solid #E2E8F0', padding: 12, backgroundColor: '#F8FAFC', display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {moduleEditor(code, mod, row)}

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                                    {mod.module !== 'weather' && (
                                        <NumberField
                                            label="Crew days" readOnly={readOnly} value={row.crewDays ?? ''}
                                            placeholder="crews × days" onChange={(v) => updateRow(code, { crewDays: v })}
                                        />
                                    )}
                                    {mod.welds && (
                                        <NumberField
                                            label={mod.weldLabel || 'Welds today'} readOnly={readOnly} value={row.welds ?? ''}
                                            onChange={(v) => updateRow(code, { welds: v })}
                                        />
                                    )}
                                    {mod.module !== 'weather' && mod.module !== 'crewDays' && (
                                        <NumberField
                                            label={`Total budget qty (${mod.uom})`} readOnly={readOnly}
                                            value={row.budgetQty ?? ''} placeholder={budgets[code] ? String(budgets[code]) : 'from tracker'}
                                            onChange={(v) => updateRow(code, { budgetQty: v })}
                                        />
                                    )}
                                </div>

                                <div>
                                    <label style={labelStyle}>Comment</label>
                                    <AutoGrowTextarea
                                        value={row.comment ?? ''}
                                        disabled={readOnly}
                                        placeholder="Anything about today's production…"
                                        onChange={(e) => updateRow(code, { comment: e.target.value })}
                                        style={{ ...inputStyle, minHeight: 40 }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {untrackedCodes.length > 0 && (
                <Text style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
                    {`No quantity entry needed for: ${untrackedCodes.map((c) => `${c}${phaseCodes?.[c] ? ' ' + phaseCodes[c] : ''}`).join(' · ')}`}
                </Text>
            )}
        </View>
    );
};

export default QuantityCards;
